import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CitizenshipFormData } from "../../../config/citizenshipFormConfig";
import api from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { extractApiErrorMessage } from "../../../utils/apiError";

interface CitizenshipFormState {
  data: Partial<CitizenshipFormData>;
  isSubmitted: boolean;
  loading: boolean;
  error: string | null;
  savePatchError?: string | null;
}

const initialState: CitizenshipFormState = {
  data: {},
  isSubmitted: false,
  loading: false,
  error: null,
  savePatchError: null,
};

// Async thunk for fetching employee details
export const fetchEmployeeDetails = createAsyncThunk(
  "citizenshipForm/fetchEmployeeDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(
        endpoints.citizenship.apply.getEmployeeDetails,
      );
      if (response.data?.success === false) {
        return rejectWithValue(
          extractApiErrorMessage({ response }, "Failed to fetch employee details"),
        );
      }
      const mappedData = {
        ...response.data,
        empShift: response.data.shiftStdHours ?? "",
        altEmail: response.data.empHomeEmail ?? "",
        altPhone: response.data.empAlternatePhone ?? "",
      };
      return mappedData;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch employee details"),
      );
    }
  },
);

// Async thunk for saving form data
export const saveFormData = createAsyncThunk(
  "citizenshipForm/saveFormData",
  async (payload: Partial<CitizenshipFormData>, { rejectWithValue }) => {
    try {
      await api.patch(endpoints.citizenship.apply.saveEmployeeDetails, payload);
      return payload;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to save form data"),
      );
    }
  },
);

// Async thunk for submitting the form
export const submitForm = createAsyncThunk(
  "citizenshipForm/submitForm",
  async (payload: Partial<CitizenshipFormData>, { rejectWithValue }) => {
    try {
      const response = await api.post(
        endpoints.citizenship.apply.submitFeeSummary,
        payload,
      );

      return {
        ...payload,
        applicationId:
          response.data?.applicationId || response.data?.application_id,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to submit form");
    }
  },
);

const citizenshipFormSlice = createSlice({
  name: "citizenshipForm",
  initialState,
  reducers: {
    clearForm: (state) => {
      state.data = {};
      state.isSubmitted = false;
    },
    clearSavePatchError(state) {
      state.savePatchError = null;
    },
  },
  extraReducers: (builder) => {
    // Handle fetchEmployeeDetails
    builder.addCase(fetchEmployeeDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchEmployeeDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(fetchEmployeeDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || null; // Set error for fetch failures
      // Don't set error for fetch failures - they shouldn't block form rendering
    });

    // Handle saveFormDataAsync
    builder.addCase(saveFormData.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.savePatchError = null;
    });
    builder.addCase(saveFormData.fulfilled, (state, action) => {
      state.loading = false;
      state.data = { ...state.data, ...action.payload };
      state.savePatchError = null;
    });
    builder.addCase(saveFormData.rejected, (state, action) => {
      state.loading = false;
      // Don't set state.error for patch errors - keep form visible with banner
      state.savePatchError = (action.payload as string) || null;
    });

    // Handle submitFormAsync
    builder.addCase(submitForm.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(submitForm.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
      state.isSubmitted = true;
    });
    builder.addCase(submitForm.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || action.error?.message || null;
    });
  },
});

export const { clearForm, clearSavePatchError } = citizenshipFormSlice.actions;
export default citizenshipFormSlice.reducer;
